import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { userAPI, programAPI, dietAPI } from '../services/api';
import {
    Box, Typography, Card, CardContent, Grid, Button,
    Alert, CircularProgress, Chip, Paper, Divider
} from '@mui/material';
import {
    Person, Email, Security, CalendarToday,
    ArrowBack, Edit, FitnessCenter, Restaurant, Badge
} from '@mui/icons-material';
import UserNavbar from '../components/UserNavbar';
import Footer from "../components/Footer";

const MyProfile = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { isDarkMode } = useTheme();

    const [userProfile, setUserProfile] = useState(null);
    const [myPrograms, setMyPrograms] = useState([]);
    const [myDiets, setMyDiets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchAllProfileData();
    }, []);

    const fetchAllProfileData = async () => {
        try {
            setLoading(true);
            setError('');

            if (!user) {
                setError('Не сте најавени.');
                setLoading(false);
                return;
            }

            const promises = [programAPI.getMyPrograms(), dietAPI.getMyDiets()];

            if (user.id) {
                promises.unshift(userAPI.getById(user.id));
            } else {
                promises.unshift(Promise.resolve(null));
            }

            const [userResult, programsResult, dietsResult] = await Promise.allSettled(promises);

            if (userResult.status === 'fulfilled' && userResult.value) {
                setUserProfile({
                    ...userResult.value,
                    firstName: userResult.value.firstName || user.firstName,
                    lastName: userResult.value.lastName || user.lastName,
                    gender: userResult.value.gender || user.gender,
                    email: userResult.value.email || user.email,
                    role: userResult.value.role || user.role,
                });
            } else {
                setUserProfile({
                    email: user.email,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    gender: user.gender,
                    role: user.role,
                    id: user.id,
                    createdAt: null,
                });
            }

            setMyPrograms(programsResult.status === 'fulfilled' ? (programsResult.value || []) : []);
            setMyDiets(dietsResult.status === 'fulfilled' ? (dietsResult.value || []) : []);

        } catch (err) {
            console.error('❌ Unexpected error:', err);
            setError('Грешка при вчитување на профилот. Обидете се повторно.');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Не е поставено';
        try {
            return new Date(dateString).toLocaleDateString('mk-MK', {
                year: 'numeric', month: 'long', day: 'numeric'
            });
        } catch {
            return 'Невалиден датум';
        }
    };

    const getRoleDisplay = (role) => ({ 'USER': 'Корисник', 'ADMIN': 'Администратор' }[role] || role);
    const getRoleColor = (role) => ({ 'USER': '#a5d8ff', 'ADMIN': '#ff7eb9' }[role] || '#666');

    const getBMICategoryColor = (category) => {
        if (!category) return '#a5d8ff';
        const c = category.toLowerCase();
        if (c.includes('норм')) return '#c8f0cc';
        if (c.includes('прекум')) return '#ffd166';
        if (c.includes('дебел') || c.includes('обез')) return '#ff6b6b';
        return '#a5d8ff';
    };

    const displayName = userProfile?.firstName && userProfile?.lastName
        ? `${userProfile.firstName} ${userProfile.lastName}`
        : userProfile?.email || 'Мојот Профил';

    // Reusable InfoRow component
    const InfoRow = ({ icon, label, value }) => (
        <Box sx={{
            display: 'flex', alignItems: 'center', gap: 2,
            py: 1.5, borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`
        }}>
            <Box sx={{ color: 'text.disabled', minWidth: 28, display: 'flex' }}>{icon}</Box>
            <Box sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    {label}
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 600 }}>
                    {value}
                </Typography>
            </Box>
        </Box>
    );

    if (loading) {
        return (
            <>
                <UserNavbar />
                <Box sx={{
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    height: '50vh', flexDirection: 'column', gap: 2,
                    backgroundColor: 'background.default'
                }}>
                    <CircularProgress sx={{ color: '#ff7eb9' }} />
                    <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                        Вчитување на профил...
                    </Typography>
                </Box>
            </>
        );
    }

    return (
        <>
            <UserNavbar />
            <Box sx={{
                py: 4, minHeight: '100vh',
                backgroundColor: 'background.default',
                transition: 'background-color 0.3s ease'
            }}>

                {/* Back Button */}
                <Box sx={{ px: { xs: 2, md: 6 }, mb: 3 }}>
                    <Button
                        onClick={() => navigate('/user-dashboard')}
                        startIcon={<ArrowBack />}
                        variant="outlined"
                        sx={{
                            color: '#ff7eb9', borderColor: '#ff7eb9', fontWeight: 'bold',
                            borderRadius: '12px', px: 3, py: 1,
                            '&:hover': { borderColor: '#ff4a97', backgroundColor: 'rgba(255,126,185,0.1)', transform: 'translateY(-2px)' },
                            transition: 'all 0.3s ease'
                        }}>
                        Назад кон Dashboard
                    </Button>
                </Box>

                {/* Header */}
                <Box sx={{ textAlign: 'center', mb: 5, px: 4 }}>
                    <Box sx={{
                        width: 90, height: 90, borderRadius: '50%', mx: 'auto', mb: 2,
                        background: 'linear-gradient(135deg, #ff7eb9, #AB47BC)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 8px 32px rgba(255,126,185,0.4)'
                    }}>
                        <Person sx={{ fontSize: 50, color: 'white' }} />
                    </Box>
                    <Typography variant="h3" sx={{ color: 'text.primary', mb: 0.5, fontWeight: 'bold', fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
                        {displayName}
                    </Typography>
                    <Chip
                        label={getRoleDisplay(userProfile?.role || user?.role)}
                        sx={{
                            bgcolor: getRoleColor(userProfile?.role || user?.role),
                            color: (userProfile?.role || user?.role) === 'USER' ? '#2b2b2b' : '#fff',
                            fontWeight: 'bold', mt: 1
                        }}
                    />
                </Box>

                {error && (
                    <Box sx={{ px: 4, mb: 4 }}>
                        <Alert severity="warning" sx={{ maxWidth: 680, mx: 'auto', borderRadius: 2 }}>{error}</Alert>
                    </Box>
                )}

                {/* Centrirani kartichki */}
                <Box sx={{ maxWidth: 680, mx: 'auto', px: { xs: 2, md: 0 }, display: 'flex', flexDirection: 'column', gap: 3 }}>

                    {/* ===== 1. ОСНОВНИ ИНФОРМАЦИИ ===== */}
                    <Card sx={{
                        borderRadius: '16px', border: '1px solid',
                        borderColor: isDarkMode ? 'rgba(255,126,185,0.3)' : 'rgba(255,126,185,0.2)',
                        backgroundColor: 'background.paper',
                        boxShadow: isDarkMode ? '0 4px 24px rgba(255,126,185,0.1)' : '0 4px 24px rgba(255,126,185,0.08)',
                        transition: 'background-color 0.3s ease'
                    }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ color: '#ff7eb9', mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                                📋 Основни Информации
                            </Typography>
                            <Divider sx={{ mb: 2, borderColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }} />

                            {(userProfile?.firstName || userProfile?.lastName) && (
                                <InfoRow
                                    icon={<Badge sx={{ fontSize: 20 }} />}
                                    label="Корисничко Име"
                                    value={`${userProfile.firstName || ''} ${userProfile.lastName || ''}`}
                                />
                            )}
                            <InfoRow
                                icon={<Email sx={{ fontSize: 20 }} />}
                                label="Email Адреса"
                                value={userProfile?.email || user?.email || 'Не е поставено'}
                            />
                            {userProfile?.gender && (
                                <InfoRow
                                    icon={<Person sx={{ fontSize: 20 }} />}
                                    label="Пол"
                                    value={userProfile.gender}
                                />
                            )}
                            {userProfile?.createdAt && (
                                <InfoRow
                                    icon={<CalendarToday sx={{ fontSize: 20 }} />}
                                    label="Членува од"
                                    value={formatDate(userProfile.createdAt)}
                                />
                            )}
                        </CardContent>
                    </Card>

                    {/* ===== 2. КУПЕНИ ПРОГРАМИ ===== */}
                    <Card sx={{
                        borderRadius: '16px', border: '1px solid',
                        borderColor: isDarkMode ? 'rgba(255,209,102,0.3)' : 'rgba(255,209,102,0.2)',
                        backgroundColor: 'background.paper',
                        boxShadow: isDarkMode ? '0 4px 24px rgba(255,209,102,0.1)' : '0 4px 24px rgba(255,209,102,0.08)',
                        transition: 'background-color 0.3s ease'
                    }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <FitnessCenter sx={{ color: '#ffd166', fontSize: 22 }} />
                                <Typography variant="h6" sx={{ color: '#ffd166', fontWeight: 'bold', flex: 1 }}>
                                    Мои Купени Програми
                                </Typography>
                                <Chip label={myPrograms.length} size="small"
                                      sx={{ bgcolor: '#ffd166', color: '#2b2b2b', fontWeight: 'bold' }} />
                            </Box>
                            <Divider sx={{ mb: 2, borderColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }} />

                            {myPrograms.length > 0 ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {myPrograms.map((up) => (
                                        <Paper key={up.id} variant="outlined" sx={{
                                            p: 2, borderRadius: '12px',
                                            border: `1px solid ${up.remainingDays > 0 ? '#ffd166' : '#ff6b6b'}`,
                                            backgroundColor: isDarkMode ? 'rgba(255,209,102,0.05)' : 'rgba(255,209,102,0.04)',
                                        }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                                                    🏋️ {up.program?.name || 'Програма'}
                                                </Typography>
                                                <Chip
                                                    label={up.remainingDays > 0 ? '✅ Активна' : '❌ Истечена'}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: up.remainingDays > 0 ? '#c8f0cc' : '#ff6b6b',
                                                        color: up.remainingDays > 0 ? '#2b2b2b' : '#fff',
                                                        fontWeight: 'bold', fontSize: '0.7rem'
                                                    }}
                                                />
                                            </Box>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                ⏱️ Преостанати денови: <strong>{up.remainingDays ?? 'N/A'}</strong>
                                            </Typography>
                                            {up.expiryDate && (
                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                    🗓️ Важи до: <strong>{formatDate(up.expiryDate)}</strong>
                                                </Typography>
                                            )}
                                        </Paper>
                                    ))}
                                </Box>
                            ) : (
                                <Alert severity="info" sx={{ borderRadius: '10px' }}>
                                    🏋️ Немате купено програми.{' '}
                                    <strong style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                            onClick={() => navigate('/buy-programs')}>
                                        Разгледајте ги програмите!
                                    </strong>
                                </Alert>
                            )}
                        </CardContent>
                    </Card>

                    {/* ===== 3. КУПЕНИ ДИЕТИ ===== */}
                    <Card sx={{
                        borderRadius: '16px', border: '1px solid',
                        borderColor: isDarkMode ? 'rgba(200,240,204,0.3)' : 'rgba(200,240,204,0.2)',
                        backgroundColor: 'background.paper',
                        boxShadow: isDarkMode ? '0 4px 24px rgba(200,240,204,0.1)' : '0 4px 24px rgba(200,240,204,0.08)',
                        transition: 'background-color 0.3s ease'
                    }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Restaurant sx={{ color: '#c8f0cc', fontSize: 22 }} />
                                <Typography variant="h6" sx={{ color: '#c8f0cc', fontWeight: 'bold', flex: 1 }}>
                                    Мои Купени Диети
                                </Typography>
                                <Chip label={myDiets.length} size="small"
                                      sx={{ bgcolor: '#c8f0cc', color: '#2b2b2b', fontWeight: 'bold' }} />
                            </Box>
                            <Divider sx={{ mb: 2, borderColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }} />

                            {myDiets.length > 0 ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {myDiets.map((ud) => (
                                        <Paper key={ud.id} variant="outlined" sx={{
                                            p: 2, borderRadius: '12px',
                                            border: `1px solid ${ud.isActive ? '#c8f0cc' : '#ff6b6b'}`,
                                            backgroundColor: isDarkMode ? 'rgba(200,240,204,0.05)' : 'rgba(200,240,204,0.04)',
                                        }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                                                    🥗 {ud.diet?.name || 'Диета'}
                                                </Typography>
                                                <Chip
                                                    label={ud.isActive ? '✅ Активна' : '❌ Неактивна'}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: ud.isActive ? '#c8f0cc' : '#ff6b6b',
                                                        color: ud.isActive ? '#2b2b2b' : '#fff',
                                                        fontWeight: 'bold', fontSize: '0.7rem'
                                                    }}
                                                />
                                            </Box>

                                            {ud.bmi && (
                                                <Box sx={{
                                                    mt: 1, mb: 1, p: 1.5, borderRadius: '8px',
                                                    border: '1px dashed #c8f0cc',
                                                    backgroundColor: isDarkMode ? 'rgba(200,240,204,0.05)' : 'rgba(200,240,204,0.08)',
                                                }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                                        📊 Мерења:
                                                    </Typography>
                                                    {ud.height && <Typography variant="body2" sx={{ color: 'text.secondary' }}>Висина: <strong>{ud.height} cm</strong></Typography>}
                                                    {ud.weight && <Typography variant="body2" sx={{ color: 'text.secondary' }}>Тежина: <strong>{ud.weight} kg</strong></Typography>}
                                                    {ud.waistCircumference && <Typography variant="body2" sx={{ color: 'text.secondary' }}>Обем на струк: <strong>{ud.waistCircumference} cm</strong></Typography>}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                            BMI: <strong>{ud.bmi?.toFixed(1)}</strong>
                                                        </Typography>
                                                        {ud.bmiCategory && (
                                                            <Chip label={ud.bmiCategory} size="small"
                                                                  sx={{ bgcolor: getBMICategoryColor(ud.bmiCategory), color: '#2b2b2b', fontWeight: 'bold', fontSize: '0.65rem' }} />
                                                        )}
                                                    </Box>
                                                </Box>
                                            )}

                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                📅 Купена на: <strong>{formatDate(ud.purchaseDate)}</strong>
                                            </Typography>
                                        </Paper>
                                    ))}
                                </Box>
                            ) : (
                                <Alert severity="info" sx={{ borderRadius: '10px' }}>
                                    🥗 Немате купено диети.{' '}
                                    <strong style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                            onClick={() => navigate('/user-diets')}>
                                        Разгледајте ги диетите!
                                    </strong>
                                </Alert>
                            )}
                        </CardContent>
                    </Card>

                    {/* ===== 4. КОПЧЕ УРЕДИ ПРОФИЛ ===== */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', pb: 4 }}>
                        <Button variant="contained" startIcon={<Edit />}
                                sx={{
                                    bgcolor: '#ff7eb9', color: '#fff', px: 5, py: 1.5,
                                    fontWeight: 'bold', borderRadius: '12px', fontSize: '1rem',
                                    '&:hover': { bgcolor: '#ff4a97', transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(255,126,185,0.4)' },
                                    transition: 'all 0.3s ease'
                                }}
                                onClick={() => alert('Функцијата за уредување на профил ќе биде достапна наскоро!')}>
                            Уреди Профил
                        </Button>
                    </Box>

                </Box>
            </Box>
            <Footer/>
        </>
    );
};

export default MyProfile;